import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';

// Animations
const electricPulse = keyframes`
  0%, 100% { opacity: 0.4; }
  50% { opacity: 1; }
`;

const dataFlow = keyframes`
  0% { transform: translateX(-100%); }
  100% { transform: translateX(100%); }
`;

const glowPulse = keyframes`
  0%, 100% { 
    box-shadow: 0 0 5px rgba(0, 212, 170, 0.5);
  }
  50% { 
    box-shadow: 0 0 20px rgba(0, 212, 170, 0.8);
  }
`;

// Styled Components
const HeaderContainer = styled.header`
  background: linear-gradient(145deg, #32423B 0%, #3F534C 50%, #32423B 100%);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  box-shadow: 0 4px 20px rgba(186, 181, 177, 0.1);
  position: sticky;
  top: 0;
  z-index: 1000;
  border-bottom: 1px solid rgba(186, 181, 177, 0.2);
  transition: all 0.3s ease;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image: url("data:image/svg+xml,%3Csvg width='100' height='100' xmlns='http://www.w3.org/2000/svg'%3E%3Cdefs%3E%3Cpattern id='circuit' x='0' y='0' width='100' height='100' patternUnits='userSpaceOnUse'%3E%3Cpath d='M0 50h30l5-5h30l5 5h30' stroke='%23BAB5B1' stroke-width='0.5' fill='none' opacity='0.2'/%3E%3Ccircle cx='50' cy='50' r='2' fill='%23BAB5B1' opacity='0.3'/%3E%3C/pattern%3E%3C/defs%3E%3Crect width='100%25' height='100%25' fill='url(%23circuit)'/%3E%3C/svg%3E");
    opacity: 0.3;
    pointer-events: none;
  }
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(90deg, transparent, #BAB5B1, #BAB5B1, #BAB5B1, transparent);
    animation: ${dataFlow} 3s linear infinite;
  }
  
  ${({ $scrolled }) => $scrolled && `
    background: linear-gradient(145deg, rgba(50, 66, 59, 0.98) 0%, rgba(63, 83, 76, 0.98) 50%, rgba(50, 66, 59, 0.98) 100%);
    backdrop-filter: blur(20px);
    box-shadow: 0 8px 32px rgba(186, 181, 177, 0.2);
  `}
`;

const HeaderContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 2rem;
  max-width: 1400px;
  margin: 0 auto;
  position: relative;
`;

const LogoLink = styled(Link)`
  text-decoration: none;
  display: flex;
  align-items: center;
  position: relative;
  z-index: 10;
  
  &::before {
    content: '';
    position: absolute;
    left: -20px;
    top: 50%;
    transform: translateY(-50%);
    width: 8px;
    height: 8px;
    background: #BAB5B1;
    border-radius: 50%;
    box-shadow: 0 0 10px #BAB5B1;
    animation: ${electricPulse} 2s ease-in-out infinite;
  }
`;

const Logo = styled.div`
  display: flex;
  align-items: center;
  
  img {
    height: 50px;
    width: auto;
    transition: all 0.3s ease;
    
    &:hover {
      transform: scale(1.05);
      filter: drop-shadow(0 0 10px rgba(186, 181, 177, 0.5));
    }
    
    @media (max-width: 768px) {
      height: 40px;
    }
  }
`;

const Nav = styled.nav`
  @media (max-width: 768px) {
    display: ${({ $isOpen }) => ($isOpen ? 'flex' : 'none')};
    position: absolute;
    top: 100%;
    left: 0;
    width: 100%;
    background: linear-gradient(145deg, rgba(50, 66, 59, 0.98) 0%, rgba(63, 83, 76, 0.98) 100%);
    backdrop-filter: blur(20px);
    flex-direction: column;
    padding: 1rem;
    border-top: 1px solid rgba(186, 181, 177, 0.2);
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  }
`;

const NavLinks = styled.ul`
  display: flex;
  list-style: none;
  margin: 0;
  padding: 0;
  align-items: center;
  
  @media (max-width: 768px) {
    flex-direction: column;
    width: 100%;
  }
`;

const NavItem = styled.li`
  margin-left: 2rem;
  position: relative;
  
  a:not(.button-link) {
    color: #EAE6E3;
    font-family: 'Inter', sans-serif;
    font-weight: 500;
    font-size: 0.95rem;
    position: relative;
    transition: all 0.3s ease;
    
    &:hover {
      color: #BAB5B1;
      text-shadow: 0 0 10px rgba(186, 181, 177, 0.5);
    }
    
    &::after {
      content: '';
      position: absolute;
      bottom: -8px;
      left: 0;
      width: 0;
      height: 2px;
      background: linear-gradient(90deg, #BAB5B1, #BAB5B1);
      box-shadow: 0 0 10px rgba(186, 181, 177, 0.5);
      transition: width 0.3s ease;
    }
    
    &:hover::after {
      width: 100%;
    }
  }
  
  @media (max-width: 768px) {
    margin: 0.5rem 0;
    width: 100%;
    text-align: center;
  }
`;

const MobileMenuButton = styled.button`
  display: none;
  background: rgba(255, 255, 255, 0.06);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(186, 181, 177, 0.3);
  border-radius: 8px;
  cursor: pointer;
  font-size: 1.5rem;
  color: #BAB5B1;
  padding: 0.5rem 0.75rem;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(186, 181, 177, 0.1);
    box-shadow: 0 0 20px rgba(186, 181, 177, 0.3);
    border-color: #BAB5B1;
  }
  
  @media (max-width: 768px) {
    display: block;
  }
`;

const ContactButton = styled(Link)`
  background: #BAB5B1;
  color: #32423B !important;
  padding: 0.6rem 1.5rem;
  border-radius: 8px;
  font-family: 'Inter', sans-serif;
  font-weight: 600;
  font-size: 0.95rem;
  margin-left: 2rem;
  text-decoration: none;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  border: 2px solid #BAB5B1;
  
  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: 0;
    height: 0;
    background: rgba(255, 255, 255, 0.2);
    border-radius: 50%;
    transform: translate(-50%, -50%);
    transition: width 0.3s ease, height 0.3s ease;
  }
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 30px rgba(186, 181, 177, 0.3);
    animation: ${glowPulse} 2s ease-in-out infinite;
    
    &::before {
      width: 100%;
      height: 100%;
    }
    
    &::after {
      width: 0 !important;
    }
  }
  
  &::after {
    display: none !important;
  }
  
  @media (max-width: 768px) {
    margin: 0.5rem 0;
    width: 100%;
    text-align: center;
  }
`;

const LoginButton = styled(Link)`
  background: rgba(255, 255, 255, 0.06);
  backdrop-filter: blur(10px);
  color: #EAE6E3 !important;
  padding: 0.6rem 1.5rem;
  border-radius: 8px;
  font-family: 'Inter', sans-serif;
  font-weight: 500;
  font-size: 0.95rem;
  margin-left: 1.5rem;
  border: 1px solid rgba(186, 181, 177, 0.3);
  text-decoration: none;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: -2px;
    left: -2px;
    right: -2px;
    bottom: -2px;
    background: linear-gradient(45deg, #BAB5B1, #EAE6E3, #BAB5B1);
    border-radius: 8px;
    opacity: 0;
    z-index: -1;
    transition: opacity 0.3s ease;
  }
  
  &:hover {
    color: #EAE6E3 !important;
    transform: translateY(-2px);
    border-color: #BAB5B1;
    box-shadow: 0 5px 20px rgba(186, 181, 177, 0.2);
    
    &::before {
      opacity: 0.3;
    }
    
    &::after {
      width: 0 !important;
    }
  }
  
  &::after {
    display: none !important;
  }
  
  @media (max-width: 768px) {
    margin: 0.5rem 0;
    width: 100%;
    text-align: center;
  }
`;

const CircuitDecoration = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 100%;
  height: 2px;
  background: linear-gradient(90deg, 
    transparent 0%, 
    rgba(186, 181, 177, 0.1) 20%, 
    rgba(186, 181, 177, 0.1) 80%, 
    transparent 100%
  );
  pointer-events: none;
  
  &::before,
  &::after {
    content: '';
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    width: 4px;
    height: 4px;
    background: #BAB5B1;
    border-radius: 50%;
    box-shadow: 0 0 10px #BAB5B1;
  }
  
  &::before {
    left: 20%;
    animation: ${electricPulse} 2s ease-in-out infinite;
  }
  
  &::after {
    right: 20%;
    animation: ${electricPulse} 2s ease-in-out infinite 0.5s;
  }
`;

const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <HeaderContainer $scrolled={scrolled}>
      <HeaderContent>
        <CircuitDecoration />
        <LogoLink to="/">
          <Logo>
            <img src="/logo/MarcaDagua03.png" alt="Veridicus Logo" />
          </Logo>
        </LogoLink>
        
        <MobileMenuButton onClick={toggleMenu}>
          {isOpen ? '✕' : '☰'}
        </MobileMenuButton>
        
        <Nav $isOpen={isOpen}>
          <NavLinks>
            <NavItem>
              <Link to="/about-us">Quem Somos</Link>
            </NavItem>
            <NavItem>
              <Link to="/nossos-servicos">Serviços</Link>
            </NavItem>
            <NavItem>
              <Link to="/faq">FAQ</Link>
            </NavItem>
            <NavItem>
              <LoginButton to="/login" className="button-link">Entrar</LoginButton>
            </NavItem>
            <NavItem>
              <ContactButton to="/fale-conosco" className="button-link">Fale Conosco</ContactButton>
            </NavItem>
          </NavLinks>
        </Nav>
      </HeaderContent>
    </HeaderContainer>
  );
};

export default Header;